/* eslint-disable @typescript-eslint/no-explicit-any */
import DataTable from "@/components/data-table";
import { ColumnVisibility } from "@/components/data-table/column-visiblity";
import { Avatar, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useLabels } from "@/hooks/use-labels";
import { usePermission } from "@/hooks/use-premission";
import {
  useAddLabelsToTemplates,
  useRemoveLabelsFromTemplates,
} from "@/hooks/use-template";
import { cn } from "@/lib/utils";
import { LabelType, PaginatedResponse } from "@/types/common";
import { TemplateType } from "@/types/template.types";
import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Check, ChevronDown, Code, Loader2, Text } from "lucide-react";
import { RefObject, useMemo, useState } from "react";
import TemplateActions from "./tempalte-actions";
import { Tag as TagIcon } from "@phosphor-icons/react";
import ScoreBadge from "@/components/score-badge";

const LabelCellRenderer = (params: any) => {
  const [isOpen, setIsOpen] = useState(false);
  const [updatingLabelId, setUpdatingLabelId] = useState<string | null>(null);
  const { mutate: addLabelsToTemplates } = useAddLabelsToTemplates();
  const { mutate: removeLabelsFromTemplates } = useRemoveLabelsFromTemplates();
  const { data: labels } = useLabels(1, 100, LabelType.TEMPLATE);

  const { hasPermission } = usePermission();

  const renderLabels = () => {
    // Check if labels exist and handle accordingly
    if (!params.data.labelIds || params.data.labelIds.length === 0) {
      return <span className="text-xs text-muted-foreground">No labels</span>;
    }

    // Get the first label if it exists
    const firstLabel = params.data.labelIds[0];

    return (
      <>
        {firstLabel && (
          <Badge
            key={firstLabel._id}
            style={{ backgroundColor: firstLabel.color }}
            className="text-background text-xs py-0.5 px-1.5 shadow-none rounded-sm"
          >
            {firstLabel.name}
          </Badge>
        )}
        {params.data.labelIds.length > 1 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge className="text-accent-foreground bg-accent text-xs py-0.5 px-1.5 shadow-none rounded-sm">
                +{params.data.labelIds.length - 1}
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="bg-black text-white">
              <div className="flex flex-col gap-1">
                {params.data.labelIds.slice(1).map((label: any) => (
                  <div key={label._id} className="flex items-center gap-2">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: label?.color }}
                    />
                    <span className="text-xs">{label?.name}</span>
                  </div>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        )}
      </>
    );
  };
  if (!hasPermission("templates:edit"))
    return (
      <div className="h-full flex items-center">
        {!params.data.labelIds?.length ? (
          <span className="text-xs text-muted-foreground">N/A</span>
        ) : (
          renderLabels()
        )}
      </div>
    );

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <div className="inline-flex w-48 justify-between h-8 px-2 rounded-md border border-input hover:bg-accent hover:text-accent-foreground transition-colors">
          <div className="flex items-center gap-1 flex-1 justify-start w-4/5">
            {!params.data.labelIds?.length ? (
              <span className="text-xs text-muted-foreground">
                Select Labels
              </span>
            ) : (
              renderLabels()
            )}
          </div>
          <div className="flex items-center gap-1 justify-center w-1/5">
            <ChevronDown className="h-4 ml-1 opacity-50" />
          </div>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[200px]">
        {labels?.data?.map((label) => (
          <DropdownMenuItem
            key={label._id}
            className="flex items-center gap-2 py-1.5"
            disabled={updatingLabelId === label._id}
            onSelect={(e) => {
              e.preventDefault();
              setUpdatingLabelId(label._id);
              const hasLabel = params.data.labelIds?.some(
                (l: any) => l._id === label._id,
              );
              if (hasLabel) {
                removeLabelsFromTemplates(
                  {
                    templateIds: [params.data._id],
                    labelIds: [label._id],
                  },
                  {
                    onSettled: () => setUpdatingLabelId(null),
                  },
                );
              } else {
                addLabelsToTemplates({
                  templateIds: [params.data._id],
                  labelIds: [label._id],
                });
              }
            }}
          >
            <div className="flex items-center gap-2 flex-1">
              <TagIcon weight="fill" size={16} style={{ fill: label.color }} />

              <span className="text-sm">{label.name}</span>
            </div>
            {updatingLabelId === label._id ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              params.data.labelIds?.some((l: any) => l._id === label._id) && (
                <Check className="h-3.5 w-3.5" />
              )
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
type Props = {
  templates?: PaginatedResponse<TemplateType>;
  isLoading: boolean;
  onRowSelect?: () => void;
  selectedRows: TemplateType[];
  ref: RefObject<AgGridReact<TemplateType> | null>;
};

const createColumnDefs = (
  // memberList: OrganizationMembership[],
): ColDef<TemplateType>[] => {
  return [
    {
      headerName: "Name",
      field: "title",
      minWidth: 200,
    },
    {
      headerName: "Subject",
      field: "subject",
      minWidth: 200,
      cellRenderer: (params: any) => {
        return (
          <div className="max-h-[100px] overflow-hidden text-ellipsis">
            {params.value?.slice(0, 100)}
          </div>
        );
      },
    },
    {
      headerName: "ContentType",
      field: "contentType",
      maxWidth: 150,
      minWidth: 150,
      cellRenderer: (params: any) => {
        return (
          <Badge className="text-accent-foreground bg-accent hover:bg-accent shadow-none rounded-sm space-x-2">
            {params.data.contentType === "html" ? (
              <>
                <Code size={12} />
                <span>HTML</span>
              </>
            ) : (
              <>
                <Text size={12} />
                <span>Plain Text</span>
              </>
            )}
          </Badge>
        );
      },
    },
    {
      field: "labelIds",
      headerName: "Labels",
      sortable: false,
      filter: false,
      minWidth: 250,
      maxWidth: 250,
      cellRenderer: LabelCellRenderer,
    },
    {
      field: "reportId",
      headerName: "Score",
      minWidth: 80,
      cellRenderer: (params: any) => {
        const score = params.data.reportId?.report?.score;
        return (
          <div className="flex items-center gap-2 h-full">
            {score ? (
              <ScoreBadge
                score={score}
                thresholds={{ green: 90, yellow: 70 }}
              />
            ) : (
              <span className="text-xs text-gray-500">N/A</span>
            )}
          </div>
        );
      },
    },
    // {
    //   field: "createdBy",
    //   headerName: "Created By",
    //   minWidth: 150,
    //   cellRenderer: (params: any) => {
    //     const member = memberList.find(
    //       (member) => member.public_metadata.externalId === params.value,
    //     );
    //     return (
    //       <div className="flex items-center gap-2">
    //         <Avatar className="w-5 h-5">
    //           <AvatarImage
    //             className="w-5 h-5"
    //             src={member?.user?.profile_picture_url}
    //           />
    //         </Avatar>
    //         <span>
    //           {member?.user?.first_name} {member?.user?.last_name}
    //         </span>
    //       </div>
    //     );
    //   },
    // },
    {
      headerName: "Created At",
      field: "createdAt",
      minWidth: 200,
      cellRenderer: (params: any) => {
        return (
          <span>
            {new Date(params.data.createdAt).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        );
      },
    },
    {
      headerName: "Updated At",
      field: "updatedAt",
      minWidth: 200,
      cellRenderer: (params: any) => {
        return (
          <span>
            {new Date(params.data.updatedAt).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        );
      },
    },
    {
      headerName: "Actions",
      width: 80,
      flex: 0,
      pinned: "right",
      filter: false,
      resizable: false,
      suppressMovable: true,
      cellRenderer: (params: any) => (
        <TemplateActions data={params.data} type="list" />
      ),
    },
  ];
};

const TemplateTableView = ({
  templates,
  isLoading,
  onRowSelect,
  ref,
}: Props) => {
  // const { memberList } = useMembersStore();
  const colDefs = useMemo(() => createColumnDefs(), []);

  return (
    <div className="flex flex-col gap-4">
      {/* <div className="flex justify-end">
        <ColumnVisibility gridRef={ref} storageKey="template-columns-state" />
      </div> */}
      <DataTable
        rowData={templates?.data || []}
        columnDefs={colDefs}
        height={550}
        showPagination={false}
        onSelectionChanged={onRowSelect}
        ref={ref}
        defaultColDef={{
          editable: false,
          resizable: false,
        }}
        loading={isLoading}
      />
    </div>
  );
};

export default TemplateTableView;
